export type InviteAckItem = {
  key: string
  label: string
  /** Missing `data-required` on older HTML stays required. */
  required: boolean
}

export const FEE_ACK_KEYS = [
  'application_fee_ack',
  'access_fee_non_refundable_ack',
  'independent_org_ack',
] as const

export const ELIGIBILITY_ACK_KEYS = [
  'ssn_itin_ack',
  'no_open_eviction_bankruptcy_ack',
] as const

export const RENTAL_ACK_KEYS = ['rental_verification_ack'] as const

function lastListItems(html: string): HTMLLIElement[] {
  if (!html || typeof DOMParser === 'undefined') return []
  const doc = new DOMParser().parseFromString(`<div id="invite-root">${html}</div>`, 'text/html')
  const root = doc.getElementById('invite-root')
  if (!root) return []
  const lists = root.querySelectorAll('ul')
  const list = lists[lists.length - 1]
  if (!list) return []
  return Array.from(list.children).filter((node): node is HTMLLIElement => node.tagName === 'LI')
}

export function listItemTexts(html: string): string[] {
  return lastListItems(html)
    .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}

export function acknowledgementRequired(node: HTMLElement): boolean {
  const flag = (node.getAttribute('data-required') || '').trim().toLowerCase()
  return flag !== '0' && flag !== 'false'
}

export function htmlWithoutLastList(html: string): string {
  if (!html || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(`<div id="invite-root">${html}</div>`, 'text/html')
  const root = doc.getElementById('invite-root')
  if (!root) return html
  const lists = root.querySelectorAll('ul')
  const last = lists[lists.length - 1]
  if (last && last.parentNode) last.parentNode.removeChild(last)
  return root.innerHTML
}

function acknowledgementItemsFromLastList(
  html: string,
  legacyKeys: readonly string[],
  extraKeyPrefix: string,
): InviteAckItem[] {
  const items: InviteAckItem[] = []
  lastListItems(html).forEach((node) => {
    const label = (node.textContent || '').replace(/\s+/g, ' ').trim()
    if (!label) return
    const index = items.length
    items.push({
      key: index < legacyKeys.length ? legacyKeys[index] : `${extraKeyPrefix}_${index}`,
      label,
      required: acknowledgementRequired(node),
    })
  })
  return items
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Intro HTML plus the acknowledgement list. Empty labels are left out of the saved HTML. */
export function composeInviteSectionHtml(
  bodyHtml: string,
  items: Array<{ label: string; required: boolean }>,
): string {
  const body = (bodyHtml || '').trim()
  const rows = items.filter((item) => item.label.trim())
  if (!rows.length) return body
  const list = `<ul>${rows
    .map(
      (item) =>
        `<li data-required="${item.required ? '1' : '0'}">${escapeHtml(item.label.trim())}</li>`,
    )
    .join('')}</ul>`
  return body ? `${body}${list}` : list
}

/** One checkbox per `<li>` in the last `<ul>` (orgs may add more than the legacy three). */
export function feeAcknowledgements(html: string): InviteAckItem[] {
  return acknowledgementItemsFromLastList(html, FEE_ACK_KEYS, 'fee_ack')
}

export function eligibilityAcknowledgements(html: string): InviteAckItem[] {
  return acknowledgementItemsFromLastList(html, ELIGIBILITY_ACK_KEYS, 'eligibility_ack')
}

export function rentalAcknowledgements(html: string): InviteAckItem[] {
  return acknowledgementItemsFromLastList(html, RENTAL_ACK_KEYS, 'rental_ack')
}

export const WELCOME_ACCEPTANCE_CTA = 'Initiate Invitation Acceptance'

/** Strip old CTA/footer lines from welcome HTML. Button label is hardcoded separately. */
export function splitWelcomeHtml(html: string): string {
  const source = (html || '').trim()
  if (!source || typeof DOMParser === 'undefined') return source
  const doc = new DOMParser().parseFromString(`<div id="invite-root">${source}</div>`, 'text/html')
  const root = doc.getElementById('invite-root')
  if (!root) return source
  Array.from(root.querySelectorAll('p')).forEach((paragraph) => {
    const text = (paragraph.textContent || '').replace(/\s+/g, ' ').trim()
    if (
      /complete the invitation acceptance wizard/i.test(text) ||
      /^initiate invitation acceptance\b/i.test(text) ||
      /^complete the invitation acceptance\b/i.test(text)
    ) {
      paragraph.remove()
    }
  })
  return root.innerHTML
}
